import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState
} from 'react';
import {
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Box,
  Typography,
  Card,
  IconButton,
  InputAdornment,
  FormHelperText
} from '@mui/material';
import Iconify from 'src/minimal-components/iconify';
import TextField from '@mui/material/TextField';
import {useFieldArray, Controller, useForm} from 'react-hook-form';
import * as yup from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';
import {useOnlineContext} from './OnlineTestContext';
import uuidv4 from 'src/utils/uuidv4';
interface Choice {
  id: string;
  OptionText: string;
  is_single_multiple_trueOrFalse_correct_option: boolean;
}

interface SingleQuestionTypeProps {
  handleEditQuestion?: any;
}

interface ChildFormMethods {
  submitForm: () => Promise<any>;
}
const schema = yup.object().shape({
  QuestionText: yup.string().required('Question is required'),
  choices: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.string().required(),
        is_single_multiple_trueOrFalse_correct_option: yup.boolean(),
        OptionText: yup.string().required('Choice is required')
      })
    )
    .min(2, 'At least two choices are required')
});
const SingleTypeQuestion = forwardRef<
  ChildFormMethods,
  SingleQuestionTypeProps
>(({handleEditQuestion}, ref) => {
  const {onlineData, setOnlineData} = useOnlineContext();
  const [hoveredItemId, setHoveredItemId] = useState<number | string | null>(
    null
  );
  const [choices, setChoices] = useState<Choice[]>(
    handleEditQuestion?.QuestionType_id === 1
    ? handleEditQuestion?.choices || [
        {
          id: 1,
          OptionText: '',
          is_single_multiple_trueOrFalse_correct_option: true
        },
        {
          id: 2,
          OptionText: '',
          is_single_multiple_trueOrFalse_correct_option: false
        }
      ]
    : [
        {
          id: 1,
          OptionText: '',
          is_single_multiple_trueOrFalse_correct_option: true
        },
        {
          id: 2,
          OptionText: '',
          is_single_multiple_trueOrFalse_correct_option: false
        }
      ]
  );
  const {
    control,
    handleSubmit,
    watch,
    clearErrors,
    setValue,
    trigger,
    getValues,
    formState: {errors}
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      QuestionText: handleEditQuestion?.QuestionText || '',
      choices: choices
    }
  });

  useEffect(() => {
    setValue('choices', choices);
    // const ValidationError = async () => await trigger('choices');
    // ValidationError();
  }, [choices]);
  useImperativeHandle(ref, () => ({
    submitForm: () =>
      new Promise((resolve, reject) => {
        handleSubmit(data => {
          resolve(data); // Resolve the promise with form data
        })();
      })
  }));
  const choicesValue = watch('choices');
  const generateUniqueId = (): string => {
    const currentIds = [
      ...(Array.isArray(handleEditQuestion?.Ipaddress)
        ? handleEditQuestion.Ipaddress.map((data: {id: any}) =>
            typeof data.id === 'string' ? data.id : ''
          )
        : []),
      ...(Array.isArray(choicesValue)
        ? choicesValue.map((field: {id: string}) => field.id)
        : [])
    ];
    if (currentIds.length > 0) {
      const maxId = Math.max(...currentIds);
      return uuidv4();
    }

    return uuidv4(); // Default to 1 if the array is empty or undefined
  };

  const handleAddChoice = async () => {
    const newId = generateUniqueId();
    if (setChoices && choices) {
      const newChoice = {
        id: newId,
        OptionText: '',
        is_single_multiple_trueOrFalse_correct_option: false
      };
      setChoices([...choices, newChoice]);
    }
    await trigger('choices');
  };

  // Handle deleting a choice
  const handleDeleteChoice = async (id: string) => {
    if (setChoices && choices) {
      const valuess = choices.filter(
        (choice: {id: string}) => choice.id !== id
      );
      setChoices(valuess);
    }

    await trigger('choices');
  };
  return (
    <Card variant="outlined" sx={{mx: 'auto', padding: '1vh'}}>
      <div>
        <Typography variant="h1" sx={{mt: 2, mb: 1}}>
          Question
        </Typography>
        <Controller
          name="QuestionText"
          control={control}
          render={({field}) => (
            <TextField
              {...field}
              fullWidth
              label="Write Question Here"
              variant="outlined"
              error={!!errors['QuestionText']}
              helperText={errors['QuestionText']?.message}
              sx={{mb: 2}}
            />
          )}
        />
      </div>
      <div>
        <FormControl component="fieldset">
          <Typography variant="h1" sx={{mb: 1}}>
            Answer
          </Typography>
          <Controller
            name="choices"
            control={control}
            render={({field}) => {
              return (
                <Box>
                  {choices?.map(
                    (
                      choice: {
                        id: string;
                        OptionText: string;
                        is_single_multiple_trueOrFalse_correct_option: boolean;
                      },
                      index: number
                    ) => {
                      return (
                        <Box
                          key={choice.id}
                          sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                          <Controller
                            name={`choices.${index}.is_single_multiple_trueOrFalse_correct_option`}
                            control={control}
                            render={({field: radioField}) => (
                              <Radio
                                {...radioField}
                                checked={
                                  choice.is_single_multiple_trueOrFalse_correct_option
                                }
                                onChange={() => {
                                  const updatedChoices = choices.map(
                                    (item, i) => ({
                                      ...item,
                                      is_single_multiple_trueOrFalse_correct_option:
                                        i === index // Set isCorrect to true for the selected option
                                    })
                                  );
                                  if (setChoices) {
                                    setChoices(updatedChoices);
                                  }
                                }}
                              />
                            )}
                          />
                          <Controller
                            name={`choices.${index}.OptionText`}
                            control={control}
                            render={({field: textField}) => (
                              <TextField
                                {...textField}
                                fullWidth
                                variant="outlined"
                                value={choice.OptionText}
                                onMouseEnter={() => setHoveredItemId(choice.id)}
                                onMouseLeave={() => setHoveredItemId(null)}
                                onChange={e => {
                                  const updatedChoices = choices.map(
                                    (item, i) =>
                                      i === index
                                        ? {
                                            ...item,
                                            OptionText: e.target.value
                                          }
                                        : item
                                  );
                                  if (setChoices) {
                                    setChoices(updatedChoices);
                                  }
                                  textField.onChange(e.target.value);
                                }}
                                onBlur={textField.onBlur}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        onClick={() =>
                                          handleDeleteChoice(choice.id)
                                        }
                                        edge="end"
                                        sx={{
                                          color: 'error.main',
                                          opacity:
                                            hoveredItemId === choice.id ? 1 : 0
                                        }}>
                                        <Iconify icon="solar:trash-bin-trash-bold" />
                                      </IconButton>
                                    </InputAdornment>
                                  )
                                }}
                                error={!!errors['choices']?.[index]?.OptionText}
                                helperText={
                                  errors['choices']?.[index]?.OptionText
                                    ?.message
                                }
                              />
                            )}
                          />
                          {/* <InputAdornment position="end">
                            {choices.length > 1 && (
                              <IconButton
                                onClick={() => handleDeleteChoice(choice.id)}
                                edge="end"
                                sx={{
                                  color: 'error.main',
                                  opacity: hoveredItemId === choice.id ? 1 : 0
                                }}>
                                <Iconify icon="solar:trash-bin-trash-bold" />
                              </IconButton>
                            )}
                          </InputAdornment> */}
                        </Box>
                      );
                    }
                  )}
                </Box>
              );
            }}
          />

          {/* {errors && (
            <FormHelperText error sx={{px: 2}}>
              {errors?.choices?.message || errors?.correctAnswer?.message}
            </FormHelperText>
          )} */}
        </FormControl>
      </div>
      <Button
        color="primary"
        onClick={() => handleAddChoice()}
        sx={{mt: 2, mb: 2}}>
        Add Choice
      </Button>
    </Card>
  );
});
export default SingleTypeQuestion;
